import { AiActionTrack } from "../../api/aiCoach";

export type PeriodPreset = "thisWeek" | "lastWeek";

export const STATUS_OPTIONS: {
  value: AiActionTrack["status"];
  label: string;
  color: string;
}[] = [
  { value: "pending", label: "未开始", color: "grey" },
  { value: "in_progress", label: "进行中", color: "blue" },
  { value: "done", label: "已完成", color: "green" },
  { value: "rejected", label: "已拒绝", color: "red" },
];

export const STATUS_COLOR_MAP: Record<string, string> = STATUS_OPTIONS.reduce(
  (acc, o) => ({ ...acc, [o.value]: o.color }),
  {}
);

/** 触发标签 → 颜色。用正则替代脆弱的 includes 字符串匹配。 */
export const TAG_COLOR_RULES: { pattern: RegExp; color: "red" | "orange" | "yellow" }[] = [
  { pattern: /高危|漏跟进|失败|倒挂|能力弱/, color: "red" },
  { pattern: /低|空转|薄弱|密集/, color: "orange" },
  { pattern: /.*/, color: "yellow" },
];

export function tagColor(tag: string): "red" | "orange" | "yellow" {
  return TAG_COLOR_RULES.find((r) => r.pattern.test(tag))?.color ?? "yellow";
}

/** 单个指标的展示契约：标签 / 释义 / 阈值 / 单位。 */
export interface MetricSpec {
  key: string;
  label: string;
  hint: string;
  unit: "pct" | "count";
  /** 方向：higher 越高越好，lower 越低越好 */
  direction: "higher" | "lower";
  /** good/warn 阈值（与 direction 组合判定颜色） */
  good: number;
  warn: number;
  /** 仅 count 类型使用：>0 即红色（漏跟进类） */
  alertOnPositive?: boolean;
}

export const METRIC_SPECS: MetricSpec[] = [
  {
    key: "totalCustomers",
    label: "总跟进",
    hint: "本周行动集客户数：有通话或有新分配的客户去重",
    unit: "count",
    direction: "higher",
    good: 10,
    warn: 5,
  },
  {
    key: "newCustomers",
    label: "新分客户",
    hint: "本周新分配到销售名下的客户数",
    unit: "count",
    direction: "higher",
    good: 5,
    warn: 2,
  },
  {
    key: "analyzedCustomers",
    label: "已 AI 画像",
    hint: "已完成意向等级判定的客户数（比率指标的分母）",
    unit: "count",
    direction: "higher",
    good: 10,
    warn: 5,
  },
  {
    key: "highStarRatio",
    label: "高星占比",
    hint: "star≥3 客户 / 已画像客户。高星占比低说明优质客户少",
    unit: "pct",
    direction: "higher",
    good: 0.4,
    warn: 0.3,
  },
  {
    key: "effectiveIntentionRate",
    label: "有效意向率",
    hint: "A+B 类客户 / 已画像客户。低于 40% 需关注分单质量",
    unit: "pct",
    direction: "higher",
    good: 0.4,
    warn: 0.3,
  },
  {
    key: "abVisitRate",
    label: "AB 上门率",
    hint: "AB 类中已签到上门的比例。反映邀约能力",
    unit: "pct",
    direction: "higher",
    good: 0.4,
    warn: 0.3,
  },
  {
    key: "resistanceRatio",
    label: "抗拒占比",
    hint: "有抗拒点的 AB 客户 / AB 总数。越高越说明话术需调整",
    unit: "pct",
    direction: "lower",
    good: 0.3,
    warn: 0.5,
  },
  {
    key: "resistanceResolutionRate",
    label: "抗拒化解率",
    hint: "有抗拒点的 AB 中已上门的比例。化解能力",
    unit: "pct",
    direction: "higher",
    good: 0.4,
    warn: 0.2,
  },
  {
    key: "lowQualityIdleRatio",
    label: "低质空转率",
    hint: "star≤1 + C/D/E 类 / 已画像。时间浪费在低质客户上",
    unit: "pct",
    direction: "lower",
    good: 0.3,
    warn: 0.5,
  },
  {
    key: "timeAllocationRatio",
    label: "时间分配比",
    hint: "高意向平均跟进次数 / 低意向平均跟进次数。<1 说明时间错配",
    unit: "pct",
    direction: "higher",
    good: 1.5,
    warn: 1.0,
  },
  {
    key: "highStarIdleCount",
    label: "高星漏跟进",
    hint: "star≥3 且分配超 3 天未跟进的客户数。>0 需立即回访",
    unit: "count",
    direction: "lower",
    good: 0,
    warn: 2,
    alertOnPositive: true,
  },
  {
    key: "highScoreIdleCount",
    label: "高分漏跟进",
    hint: "score>65 且分配超 3 天未跟进的客户数",
    unit: "count",
    direction: "lower",
    good: 0,
    warn: 2,
    alertOnPositive: true,
  },
];
